// ViewManager
// Handles view navigation, URL state, and navigation history

export type ViewType = 'notes' | 'knowledge' | 'inbox' | 'outreach' | 'patterns' |
                       'profiles' | 'settings' | 'vault' | 'audience' | 'review' |
                       'classification' | 'stats';

export interface NavigationState {
  view: 'inbox' | 'knowledge' | 'projects' | 'notes';
  scrollPosition?: number;
  selectedId?: string;
}

export interface ViewManagerCallbacks {
  loadView: (view: string) => Promise<void>;
  syncSidebarTab: (view: string) => void;
  getActiveTabId: () => string | null;
  getActiveTab: () => string;
  updateAIVisibility: () => void;
  getSelectedId: (view: string) => string | undefined;
}

const VIEW_MAP: Record<string, string> = {
  'notes': 'editor-view',
  'knowledge': 'knowledge-view',
  'inbox': 'inbox-view',
  'outreach': 'outreach-view',
  'patterns': 'patterns-view',
  'profiles': 'profiles-view',
  'settings': 'settings-view',
  'vault': 'vault-view',
  'audience': 'audience-view',
  'review': 'review-view',
  'classification': 'classification-queue-view',
  'stats': 'stats-view'
};

const SCROLL_CONTAINERS: Record<string, string> = {
  'inbox': '#inbox-list',
  'knowledge': '#kb-list',
  'projects': '#projects-list'
};

const VALID_URL_VIEWS = ['inbox', 'knowledge', 'outreach', 'patterns', 'profiles', 'settings', 'vault'];

export class ViewManager {
  private currentView: string = 'notes';
  private navigationHistory: NavigationState[] = [];
  private maxNavigationHistory = 20;
  private backBtn: HTMLElement | null = null;
  private callbacks: ViewManagerCallbacks;

  constructor(callbacks: ViewManagerCallbacks) {
    this.callbacks = callbacks;
    this.backBtn = document.getElementById('btn-nav-back');
  }

  getCurrentView(): string {
    return this.currentView;
  }

  /**
   * Setup back navigation button and keyboard shortcut
   */
  setupBackNavigation(): void {
    this.backBtn?.addEventListener('click', () => {
      this.navigateBack();
    });

    document.addEventListener('keydown', (e) => {
      if (e.altKey && e.key === 'ArrowLeft') {
        e.preventDefault();
        this.navigateBack();
      }
    });
  }

  /**
   * Switch to a specific view
   */
  async switchToView(view: string): Promise<void> {
    const viewId = VIEW_MAP[view] || 'editor-view';

    this.currentView = view;

    // Hide all main views
    document.querySelectorAll('.main-view').forEach(v => {
      v.classList.add('hidden');
    });

    // Show target view
    const targetView = document.getElementById(viewId);
    if (targetView) {
      targetView.classList.remove('hidden');
    }

    // Load view content via callback
    await this.callbacks.loadView(view);

    // Sync sidebar tab state
    this.callbacks.syncSidebarTab(view);

    // Update URL
    if (view !== 'notes') {
      this.updateUrl(view);
    } else {
      const activeDocId = this.callbacks.getActiveTabId();
      this.updateUrl(view, activeDocId || undefined);
    }

    // Update AI visibility
    this.callbacks.updateAIVisibility();
  }

  /**
   * Update URL with current view and document
   */
  updateUrl(view?: string, docId?: string): void {
    const url = new URL(window.location.href);

    if (view && view !== 'notes') {
      url.searchParams.set('view', view);
    } else {
      url.searchParams.delete('view');
    }

    if (docId) {
      url.searchParams.set('doc', docId);
    } else {
      url.searchParams.delete('doc');
    }

    window.history.replaceState({}, '', url.toString());
  }

  /**
   * Get URL parameters
   */
  getUrlParams(): { view: string | null; docId: string | null; resourceId: string | null } {
    const url = new URL(window.location.href);
    return {
      view: url.searchParams.get('view'),
      docId: url.searchParams.get('doc'),
      resourceId: url.searchParams.get('resource')
    };
  }

  /**
   * Check if view is valid for URL restoration
   */
  isValidUrlView(view: string): boolean {
    return VALID_URL_VIEWS.includes(view);
  }

  /**
   * Clear invalid param from URL
   */
  clearUrlParam(param: string): void {
    const url = new URL(window.location.href);
    url.searchParams.delete(param);
    window.history.replaceState({}, '', url.toString());
  }

  /**
   * Push current navigation state to history
   */
  pushNavigationState(): void {
    const currentView = this.callbacks.getActiveTab() as 'inbox' | 'knowledge' | 'projects' | 'notes';

    let scrollPosition = 0;
    const container = document.querySelector(SCROLL_CONTAINERS[currentView]);
    if (container) {
      scrollPosition = container.scrollTop;
    }

    // Don't push duplicate consecutive entries
    const last = this.navigationHistory[this.navigationHistory.length - 1];
    if (last?.view === currentView) return;

    this.navigationHistory.push({
      view: currentView,
      scrollPosition,
      selectedId: this.callbacks.getSelectedId(currentView)
    });

    if (this.navigationHistory.length > this.maxNavigationHistory) {
      this.navigationHistory.shift();
    }

    this.updateBackButton();
  }

  /**
   * Navigate back to previous view
   */
  async navigateBack(): Promise<boolean> {
    if (this.navigationHistory.length === 0) return false;

    const prev = this.navigationHistory.pop();
    if (!prev) return false;

    await this.switchToView(prev.view);

    // Restore scroll position after render
    setTimeout(() => {
      const container = document.querySelector(SCROLL_CONTAINERS[prev.view]);
      if (container && prev.scrollPosition) {
        container.scrollTop = prev.scrollPosition;
      }

      if (prev.selectedId) {
        this.restoreSelection(prev.view, prev.selectedId);
      }
    }, 50);

    this.updateBackButton();
    return true;
  }

  /**
   * Restore selection in a view
   */
  private restoreSelection(view: string, id: string): void {
    switch (view) {
      case 'knowledge':
        const kbItem = document.querySelector(`.kb-item[data-id="${id}"]`);
        kbItem?.classList.add('selected');
        kbItem?.scrollIntoView({ block: 'nearest' });
        break;
      case 'inbox':
        const inboxItem = document.querySelector(`.inbox-card[data-id="${id}"]`);
        inboxItem?.classList.add('selected');
        break;
    }
  }

  /**
   * Update back button visibility
   */
  private updateBackButton(): void {
    if (this.backBtn) {
      this.backBtn.classList.toggle('hidden', this.navigationHistory.length === 0);
    }
  }

  /**
   * Check if there's history to go back to
   */
  hasHistory(): boolean {
    return this.navigationHistory.length > 0;
  }
}
